import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { useValuationStore } from '@/store/valuationStore'
import { cn } from '@/utils/cn'
import logo from '@/assets/vvslogo.png'
import { Eye, EyeOff } from 'lucide-react'
import { Footer } from '@/components/layout/Footer'
import { PageLoader } from '@/components/ui/LoadingSpinner'
import { getMessage } from '@/utils/messages'
import { log } from '@/utils/logger'

export function LoginPage() {
  const navigate = useNavigate()
  const login = useAuthStore((s) => s.login)

  const [email, setEmail] = useState('')
  const [officeId, setOfficeId] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; officeId?: string; password?: string }>({})
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const validateFields = () => {
    const errors: { email?: string; officeId?: string; password?: string } = {}
    if (!email.trim()) {
      errors.email = getMessage('login.error_email_required')
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errors.email = getMessage('login.error_email_invalid')
    }
    if (!officeId.trim()) {
      errors.officeId = getMessage('login.error_office_id_required')
    }
    if (!password) {
      errors.password = getMessage('login.error_password_required')
    }
    return errors
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)

    const errors = validateFields()
    if (Object.keys(errors).length > 0) {
      log.warn('login validation failed', { fields: Object.keys(errors).join(',') })
      setFieldErrors(errors)
      return
    }
    setFieldErrors({})
    setLoading(true)

    // If a different user is logging in, wipe the previous user's local draft
    // before calling login so we don't carry stale data into the new session.
    const prevUser = useAuthStore.getState().user
    if (prevUser && prevUser.email !== email.trim()) {
      useValuationStore.getState().clearLocalDraft()
    }

    const result = await login(email, officeId, password)

    if (!result.success) {
      setLoading(false)
      const codeMessages: Record<string, string> = {
        MISSING_FIELDS: getMessage('login.all_fields_required'),
        INACTIVE_USER: getMessage('login.inactive_user'),
        INVALID_CREDENTIALS: getMessage('login.invalid_user_password'),
        MISSING_TOKEN: getMessage('login.invalid_credentials'),
        ACCOUNT_LOCKED: getMessage('login.account_locked'),
        RATE_LIMIT_EXCEEDED: getMessage('login.rate_limit_exceeded'),
        BACKEND_UNAVAILABLE: getMessage('login.backend_unavailable') ,
      }
      setError((result.code && codeMessages[result.code]) ?? getMessage('login.error_default'))
      return
    }

    if (result.success) {
      log.info('login success — navigating to dashboard')
      setLoading(false)
      navigate('/dashboard', { replace: true })
    }
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: 'var(--bg)' }}
    >
      {loading && <PageLoader label={getMessage('login.loading_label')} />}
      {/* Background texture (same as app body::before) */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 80% 50% at 20% 0%, rgba(61,142,240,0.06) 0%, transparent 60%),
            radial-gradient(ellipse 60% 40% at 80% 100%, rgba(13,168,106,0.05) 0%, transparent 50%),
            repeating-linear-gradient(0deg, transparent, transparent 39px, rgba(0,0,0,0.022) 40px),
            repeating-linear-gradient(90deg, transparent, transparent 39px, rgba(0,0,0,0.022) 40px)
          `,
        }}
      />

      {/* Centered content area */}
      <div className="flex-1 flex items-center justify-center px-4">

      {/* Login card */}
      <div
        className={cn(
          'relative z-10 w-full max-w-[400px]',
          'bg-[var(--surface)] border border-[var(--border)]',
          'rounded-[var(--radius)] shadow-[0_8px_40px_rgba(0,0,0,0.10)]',
          'animate-fade-in'
        )}
      >
        {/* Card header */}
        <div className="px-8 pt-8 pb-6 border-b border-[var(--border)] flex flex-col items-center gap-3">
          <div className="w-[52px] h-[52px] rounded-[12px] flex items-center justify-center border border-[var(--border)] bg-[var(--surface2)]">
            <img src={logo} alt="VVSI Logo" className="w-[38px] h-[38px] object-contain" />
          </div>
          <div className="text-center">
            <p className="text-[12px] text-[var(--text-muted)] mt-[2px]">
              {getMessage('login.app_subtitle')}
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-8 py-6 flex flex-col gap-[18px]">

          {/* Email */}
          <div className="flex flex-col gap-[5px]">
            <label className="text-[11px] font-semibold uppercase tracking-[0.6px] text-[var(--text-label)]">
              {getMessage('login.label_email')}
            </label>
            <input
              type="email"
              className={cn('field-input', fieldErrors.email && 'border-[var(--error)]')}
              placeholder={getMessage('login.placeholder_email')}
              value={email}
              onChange={(e) => { setEmail(e.target.value); setFieldErrors((prev) => ({ ...prev, email: undefined })) }}
              autoComplete="email"
              autoFocus
            />
            {fieldErrors.email && (
              <span className="text-[11px] text-[var(--error)] mt-[2px]">{fieldErrors.email}</span>
            )}
          </div>

          {/* Office ID */}
          <div className="flex flex-col gap-[5px]">
            <label className="text-[11px] font-semibold uppercase tracking-[0.6px] text-[var(--text-label)]">
              {getMessage('login.label_office_id')}
            </label>
            <input
              type="text"
              className={cn('field-input', fieldErrors.officeId && 'border-[var(--error)]')}
              placeholder={getMessage('login.placeholder_office_id')}
              value={officeId}
              onChange={(e) => { setOfficeId(e.target.value); setFieldErrors((prev) => ({ ...prev, officeId: undefined })) }}
              autoComplete="organization"
            />
            {fieldErrors.officeId && (
              <span className="text-[11px] text-[var(--error)] mt-[2px]">{fieldErrors.officeId}</span>
            )}
          </div>

          {/* Password */}
          <div className="flex flex-col gap-[5px]">
            <label className="text-[11px] font-semibold uppercase tracking-[0.6px] text-[var(--text-label)]">
              {getMessage('login.label_password')}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                className={cn('field-input pr-[40px]', fieldErrors.password && 'border-[var(--error)]')}
                placeholder={getMessage('login.placeholder_password')}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setFieldErrors((prev) => ({ ...prev, password: undefined })) }}
                autoComplete="current-password"
              />
              <button
                type="button"
                tabIndex={-1}
                onMouseDown={() => setShowPassword(true)}
                onMouseUp={() => setShowPassword(false)}
                onMouseLeave={() => setShowPassword(false)}
                className={cn(
                  'absolute right-[10px] top-1/2 -translate-y-1/2',
                  'text-[var(--text-muted)] text-[13px] cursor-pointer',
                  'hover:text-[var(--text)] transition-colors duration-150'
                )}
                aria-label={showPassword ? getMessage('login.aria_hide_password') : getMessage('login.aria_show_password')}
              >
                {showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
              </button>
            </div>
            {fieldErrors.password && (
              <span className="text-[11px] text-[var(--error)] mt-[2px]">{fieldErrors.password}</span>
            )}
          </div>

          {/* Error message */}
          {error && (
            <div
              className={cn(
                'flex items-center gap-[8px] px-3 py-[9px]',
                'rounded-[var(--radius-sm)] text-[12px]',
                'bg-[rgba(220,38,38,0.06)] border border-[rgba(220,38,38,0.25)]',
                'text-[var(--error)]'
              )}
            >
              <span className="flex-shrink-0">⚠</span>
              {error}
            </div>
          )}

          {/* Login button */}
          <button
            type="submit"
            disabled={loading}
            className={cn(
              'w-full py-[11px] rounded-[var(--radius-sm)]',
              'text-white text-[13px] font-semibold',
              'border border-transparent',
              'transition-all duration-[180ms]',
              loading
                ? 'opacity-60 cursor-not-allowed' 
                : 'hover:opacity-90 cursor-pointer'
            )}
            style={{
              background: 'linear-gradient(135deg, var(--accent), #2563eb)',
            }}
          >
            {loading ? getMessage('login.button_signing_in') : getMessage('login.button_login')}
          </button>

          {/* Demo hint */}
          {/* <p className="text-center text-[11px] text-[var(--text-muted)]">
            Demo — use <span className="font-semibold text-[var(--text-dim)]">test@test.com</span> /{' '}
            <span className="font-semibold text-[var(--text-dim)]">test</span> /{' '}
            <span className="font-semibold text-[var(--text-dim)]">test</span>
          </p> */}
        </form>
      </div>

      </div>{/* end centered content area */}

      <Footer />
    </div>
  )
}